import { useNavigate, Link } from 'react-router-dom'
import {
  Backpack,
  Hammer,
  PackageOpen,
  Trophy,
  Coins,
  TrendingUp,
  Warehouse,
  CloudOff,
  Heart,
  Zap,
  Drumstick,
  Droplets,
  ArrowRight,
} from 'lucide-react'
import AnimatedModal from '../common/AnimatedModal'
import { getItemImagePath } from '../../utils/itemImagePath'
import { formatTimeLong } from '../../utils/timeFormatters'
import { formatCurrency, formatChange } from '../../utils/currencyFormatters'

const SECTION_LINK_CLASS =
  'inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-accent-blue hover:text-accent-blue-light transition-colors'

/**
 * A survived run row states what HAPPENED, never a live status the digest
 * cannot know (the old "Awaiting extraction" claimed state it never checked —
 * the player may have extracted long before this modal opened).
 */
function survivedRunLabel(run) {
  const items = `${formatCurrency(run.loot_count)} ${run.loot_count === 1 ? 'item' : 'items'}`
  const currency = run.currency_gained > 0 ? `, +${formatCurrency(run.currency_gained)} cr` : ''
  return `Completed — ${items}${currency}`
}

function SectionHeader({ icon: Icon, title, to, linkLabel }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-text-primary">
        <Icon size={16} className="text-accent-yellow" aria-hidden="true" /> {title}
      </h3>
      {to && (
        <Link to={to} className={SECTION_LINK_CLASS} aria-label={`View ${title}`}>
          {linkLabel || 'View'} <ArrowRight size={12} aria-hidden="true" />
        </Link>
      )}
    </div>
  )
}

function LootRow({ item }) {
  return (
    <li className="flex items-center gap-2.5 py-1">
      <img
        src={getItemImagePath({ item_id: item.item_id, ...item })}
        alt={item.name}
        className="w-7 h-7 rounded-md border border-border-primary bg-bg-secondary object-cover"
        loading="lazy"
      />
      <span className="flex-1 truncate text-sm text-text-secondary">{item.name}</span>
      <span className="text-sm font-bold text-text-primary tabular-nums">
        {formatCurrency(item.quantity)}
      </span>
    </li>
  )
}

function StatDelta({ icon: Icon, label, value }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <Icon size={14} className="text-text-muted" aria-hidden="true" />
      <span className="text-text-muted">{label}</span>
      <span className="font-bold tabular-nums text-text-primary">
        {typeof value === 'number' ? formatChange(value) : '—'}
      </span>
    </span>
  )
}

/**
 * "While you were away" digest modal (offline catch-up, plan Slice 6).
 * Rendered once per session by AuthenticatedLayout when the digest has content.
 */
function CatchUpDigestModal({ digest, isOpen, onDismiss }) {
  const navigate = useNavigate()

  // Defense in depth: the hook gates on has_content, but an empty digest must
  // never render even if isOpen is forced true.
  if (!digest || !isOpen || digest.has_content === false) return null

  const runs = digest.runs_completed || []
  const gathering = digest.gathering || null
  const crafting = digest.crafting_completed || []
  const upgrades = digest.upgrades_completed || []
  const stationUpgrades = digest.station_upgrades_completed || []
  const expansions = digest.expansions_completed || []
  const achievements = digest.achievements_unlocked || []
  const marketSales = digest.market_sales || []
  const levelUps = digest.level_ups || []
  const stats = digest.stats_delta || null

  const handleBackToShelter = () => {
    onDismiss()
    navigate('/shelter')
  }

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onDismiss}
      title="While you were away"
      subtitle={typeof digest.away_seconds === 'number' ? `Gone for ${formatTimeLong(digest.away_seconds)}` : null}
      size="lg"
      className="max-h-[85vh]"
      footer={
        <button
          type="button"
          onClick={handleBackToShelter}
          data-testid="catchup-cta"
          className="w-full py-3 rounded-xl font-extrabold uppercase tracking-[0.14em] text-sm text-text-inverse bg-gradient-to-b from-accent-yellow-light to-accent-yellow border border-accent-yellow-dark/50 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(0,0,0,0.4)]"
        >
          Back to the bunker
        </button>
      }
    >
      <div className="overflow-y-auto flex-grow px-6 py-4 space-y-5" data-testid="catchup-digest-body">
        {runs.length > 0 && (
          <section className="space-y-2">
            <SectionHeader icon={Backpack} title="Scavenging runs" to="/scavenging" linkLabel="Completed while you were away" />
            <ul className="space-y-1.5">
              {runs.map((run) => (
                <li
                  key={run.run_id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border-primary bg-bg-secondary/60 px-3 py-2"
                >
                  <span className="text-sm font-semibold text-text-primary truncate">
                    {run.map_name || 'Unknown region'}
                  </span>
                  {run.died ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-accent-red-light shrink-0">
                      <PackageOpen size={14} aria-hidden="true" /> Died — loadout lost
                    </span>
                  ) : (
                    <Link to="/scavenging" className="inline-flex items-center gap-1.5 text-xs font-bold text-accent-yellow shrink-0">
                      <ArrowRight size={14} aria-hidden="true" /> {survivedRunLabel(run)}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {gathering && (
          <section className="space-y-2">
            <SectionHeader icon={Hammer} title="Gathering work" to="/gathering" linkLabel="Open gatherings" />
            <div className="rounded-lg border border-border-primary bg-bg-secondary/60 px-3 py-2.5 space-y-2">
              <p className="text-sm text-text-primary">
                <span className="font-semibold">{gathering.gathering_name}</span> worked{' '}
                <span className="font-bold tabular-nums">{formatCurrency(gathering.ticks_processed)}</span> sweeps
                {gathering.stop_reason ? (
                  <span className="text-text-muted"> — stopped ({String(gathering.stop_reason).replace(/_/g, ' ')})</span>
                ) : null}
              </p>
              {gathering.loot?.length > 0 && (
                <ul className="divide-y divide-border-dark">
                  {gathering.loot.map((item) => (
                    <LootRow key={item.item_id} item={item} />
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}

        {(crafting.length > 0 || upgrades.length > 0 || stationUpgrades.length > 0) && (
          <section className="space-y-2">
            <SectionHeader icon={Hammer} title="Crafting finished" to="/crafting" />
            <ul className="space-y-1.5">
              {crafting.map((job) => (
                <li key={job.job_id} className="text-sm text-text-secondary">
                  <span className="font-semibold text-text-primary">{job.recipe_name}</span>
                  {job.quantity > 1 && <span className="tabular-nums"> × {formatCurrency(job.quantity)}</span>}
                </li>
              ))}
              {upgrades.map((job) => (
                <li key={job.job_id} className="text-sm text-text-secondary">
                  <span className="font-semibold text-text-primary">{job.item_name}</span> upgraded
                </li>
              ))}
              {stationUpgrades.map((job) => (
                <li key={job.job_id} className="text-sm text-text-secondary">
                  <span className="font-semibold text-text-primary">{job.item_name}</span> station improved
                </li>
              ))}
            </ul>
          </section>
        )}

        {(expansions.length > 0 || digest.production_ready_count > 0) && (
          <section className="space-y-2">
            <SectionHeader icon={Warehouse} title="Shelter work" to="/rooms" />
            <ul className="space-y-1.5">
              {expansions.map((exp) => (
                <li key={exp.expansion_id} className="text-sm text-text-secondary">
                  Room expanded to{' '}
                  <span className="font-bold text-text-primary tabular-nums">
                    {exp.new_width}×{exp.new_height}
                  </span>
                </li>
              ))}
              {digest.production_ready_count > 0 && (
                <li className="text-sm text-text-secondary">
                  <span className="font-bold text-text-primary tabular-nums">
                    {formatCurrency(digest.production_ready_count)}
                  </span>{' '}
                  production jobs finished — ready in your rooms
                </li>
              )}
            </ul>
          </section>
        )}

        {achievements.length > 0 && (
          <section className="space-y-2">
            <SectionHeader icon={Trophy} title="Achievements" to="/achievements" />
            <ul className="space-y-1.5">
              {achievements.map((ach) => (
                <li key={ach.achievement_id} className="text-sm text-text-secondary">
                  <span className="font-semibold text-text-primary">{ach.name}</span>
                  {ach.rarity && <span className="text-text-muted"> · {ach.rarity}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {marketSales.length > 0 && (
          <section className="space-y-2">
            <SectionHeader icon={Coins} title="Market sales" to="/market" />
            <ul className="space-y-1.5">
              {marketSales.map((sale) => (
                <li key={sale.listing_id} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-text-secondary truncate">
                    <span className="font-semibold text-text-primary">{sale.item_name}</span>
                    {sale.quantity > 1 && <span className="tabular-nums"> × {formatCurrency(sale.quantity)}</span>}
                  </span>
                  <span className="inline-flex items-center gap-1 font-bold text-accent-yellow tabular-nums shrink-0">
                    <Coins size={14} aria-hidden="true" /> {formatCurrency(sale.price)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {levelUps.length > 0 && (
          <section className="space-y-2">
            <SectionHeader icon={TrendingUp} title="Level ups" />
            <ul className="space-y-1.5">
              {levelUps.map((lvl, i) => (
                <li key={`${lvl.new_level}-${i}`} className="text-sm text-text-secondary">
                  Reached level <span className="font-bold text-text-primary tabular-nums">{formatCurrency(lvl.new_level)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-border-primary bg-bg-secondary/60 px-3 py-2.5">
          <span className="text-xs font-bold uppercase tracking-wider text-text-muted w-full sm:w-auto">
            While away
          </span>
          <StatDelta icon={Heart} label="Health" value={stats ? stats.health : undefined} />
          <StatDelta icon={Zap} label="Stamina" value={stats ? stats.stamina : undefined} />
          <StatDelta icon={Drumstick} label="Hunger" value={stats ? stats.hunger : undefined} />
          <StatDelta icon={Droplets} label="Hydration" value={stats ? stats.hydration : undefined} />
        </section>

        {digest.inbox_available === false && (
          <p className="flex items-center gap-1.5 text-xs text-text-muted" data-testid="catchup-inbox-unavailable">
            <CloudOff size={12} aria-hidden="true" /> Event log unavailable — some gathering may be missing from this report.
          </p>
        )}
      </div>
    </AnimatedModal>
  )
}

export default CatchUpDigestModal
